import { Injectable } from '@angular/core';
import { AppTextService } from '@fi-shared/app-text';
import { TextEditorHistoryService } from '@fi-shared/fi-text-editor/fi-text-editor-helper-services/text-editor-history.service';
import { ITextPanelButton, TextPanelMenuTypes } from '@fi-shared/fi-text-panel/simple-text-panel/text-panel-base';
import { FiApplicationLanguageType } from '@fi-shared/fi-content-language-shared/fi-application-language-type';
import { BehaviorSubject } from 'rxjs';
import { isDefined } from '@fi-shared/utils';

export enum TextPanelAlignmentCommandName {
    Left = 'justifyLeft',
    Center = 'justifyCenter',
    Right = 'justifyRight',
    Full = 'justifyFull',
}

export enum TextPanelAlignmentIconClassName {
    Left = 'fas fa-align-left',
    Center = 'fas fa-align-center',
    Right = 'fas fa-align-right',
    Full = 'fas fa-align-justify',
}

export enum TextPanelHtmlElementAlignmentStyleValue {
    Left = 'left',
    Center = 'center',
    Right = 'right',
    Full = 'justify',
}

@Injectable()
export class AdvancedTextPanelJustifyService {
    private readonly htmlElementAlignmentToIconClassCollection: {
        readonly [key in TextPanelHtmlElementAlignmentStyleValue]: TextPanelAlignmentIconClassName;
    } = {
        [TextPanelHtmlElementAlignmentStyleValue.Left]: TextPanelAlignmentIconClassName.Left,
        [TextPanelHtmlElementAlignmentStyleValue.Center]: TextPanelAlignmentIconClassName.Center,
        [TextPanelHtmlElementAlignmentStyleValue.Right]: TextPanelAlignmentIconClassName.Right,
        [TextPanelHtmlElementAlignmentStyleValue.Full]: TextPanelAlignmentIconClassName.Full,
    } as const;

    private readonly alignmentCommandNameToAlignmentStyleCollection: {
        readonly [key in TextPanelAlignmentCommandName]: TextPanelHtmlElementAlignmentStyleValue;
    } = {
        [TextPanelAlignmentCommandName.Left]: TextPanelHtmlElementAlignmentStyleValue.Left,
        [TextPanelAlignmentCommandName.Center]: TextPanelHtmlElementAlignmentStyleValue.Center,
        [TextPanelAlignmentCommandName.Right]: TextPanelHtmlElementAlignmentStyleValue.Right,
        [TextPanelAlignmentCommandName.Full]: TextPanelHtmlElementAlignmentStyleValue.Full,
    } as const;

    constructor(
        private appTextService: AppTextService,
        private textEditorHistoryService: TextEditorHistoryService,
    ) {}

    private htmlElementAlignment$ = new BehaviorSubject<string>(undefined);
    updateHtmlElementAlignment(alignment: string) {
        this.htmlElementAlignment$.next(alignment);
    }

    getButtons(langId: number): ITextPanelButton[] {
        return [
            {
                title: this.appTextService.get('alignment.alignment'),
                class: (): string => {
                    const currentAlignment: string = this.htmlElementAlignment$.value;
                    if (isDefined(currentAlignment))
                        return this.htmlElementAlignmentToIconClassCollection[currentAlignment];

                    return langId === FiApplicationLanguageType.Arabic ? 'fas fa-align-right' : 'fas fa-align-left';
                },
                btnClass: 'mobile-extended BrandedColorHover panel-icon-button',
                isMatMenu: true,
                matMenuTriggerButtonType: TextPanelMenuTypes.Alignment,
                callback: (command: string) => {
                    this.textEditorHistoryService.saveState();
                    document.execCommand(command, false, null);
                    this.textEditorHistoryService.restoreFocus();
                    this.updateHtmlElementAlignment(this.alignmentCommandNameToAlignmentStyleCollection[command]);
                },
                menuItems: [
                    {
                        iconClass: 'fas fa-align-left',
                        label: 'alignment.left',
                        value: TextPanelAlignmentCommandName.Left,
                    },
                    {
                        iconClass: 'fas fa-align-center',
                        label: 'alignment.center',
                        value: TextPanelAlignmentCommandName.Center,
                    },
                    {
                        iconClass: 'fas fa-align-right',
                        label: 'alignment.right',
                        value: TextPanelAlignmentCommandName.Right,
                    },
                    {
                        iconClass: 'fas fa-align-justify',
                        label: 'alignment.justified',
                        value: TextPanelAlignmentCommandName.Full,
                    },
                ],
            },
        ];
    }
}
